import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './ProjectApi';
import { Project, ProjectRequest, ProjectSearchRequest, ReclaimRequest } from './ProjectTypes';

const projectKey = getQueryKey('project');

export function getProjectPagedOptions(params: PagedParams): UseSuspenseQueryOptions<PagedResponse<Project>> {
  return {
    queryKey: projectKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getProjectOptions(projectId: Id): UseSuspenseQueryOptions<Project> {
  return {
    queryKey: projectKey.one(projectId),
    queryFn: () => api.get(projectId),
  };
}

export function useProjectEdit(projectId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ProjectRequest) => api.edit(projectId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: projectKey.root(),
      }),
  });
}

export function useProjectCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ProjectRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: projectKey.root(),
      }),
  });
}

export function useProjectDelete(projectId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(projectId),
    onSuccess: () => invalidateAfterDelete(queryClient, projectKey, projectId),
  });
}

export function useSearchProjectByIdentifiers() {
  return useMutation({
    mutationFn: (request: ProjectSearchRequest) => api.searchByIdentifiers(request),
  });
}

export function useReclaimAuthorship() {
  return useMutation({
    mutationFn: ({ projectId, authorId }: ReclaimRequest) => api.reclaimAuthorship(projectId, authorId),
  });
}
